<template>
  <div class="fb-demo">
    <aside class="fb-demo__sidebar">
      <h5 class="fb-demo__title">PDF Form Builder — Demo</h5>

      <div class="fb-demo__mode">
        <b-button
          size="sm"
          :variant="mode === 'editor' ? 'primary' : 'outline-primary'"
          @click="mode = 'editor'"
        >
          Editor
        </b-button>
        <b-button
          size="sm"
          :variant="mode === 'fill' ? 'primary' : 'outline-primary'"
          :disabled="!fields.length"
          @click="mode = 'fill'"
        >
          Completar
        </b-button>
      </div>

      <label class="fb-demo__label">URL del PDF</label>
      <div class="fb-demo__url">
        <input
          v-model="urlInput"
          class="fb-demo__input"
          type="text"
          placeholder="https://.../documento.pdf"
        />
        <b-button size="sm" variant="primary" @click="loadUrl">Cargar</b-button>
      </div>

      <!-- ===== Panel de EDITOR ===== -->
      <template v-if="mode === 'editor'">
      <label class="fb-demo__label">Tipo de campo a crear</label>
      <div class="fb-demo__palette">
        <b-button
          v-for="t in fieldTypeList"
          :key="t.type"
          size="sm"
          :variant="t.type === activeFieldType ? 'primary' : 'outline-primary'"
          class="fb-demo__palette-btn"
          @click="toggleType(t.type)"
        >
          {{ t.label }}
        </b-button>
      </div>
      <p class="fb-demo__hint">
        <template v-if="activeFieldType">
          Haz click sobre el PDF para crear un campo
          <strong>{{ activeLabel }}</strong>. Se agrega de a uno.
        </template>
        <template v-else>
          Selecciona un tipo para agregar un campo.
        </template>
      </p>

      <div class="fb-demo__actions">
        <b-button size="sm" variant="outline-danger" @click="clearFields">
          Vaciar campos
        </b-button>
        <span class="fb-demo__count">{{ fields.length }} campo(s)</span>
      </div>

      <label class="fb-demo__label">Campos agregados</label>
      <FieldListPanel
        class="fb-demo__list"
        :fields="fields"
        :selected-id="selectedId"
        @select="selectedId = $event"
        @delete="deleteField"
        @update="updateField"
      />

      <label class="fb-demo__label">template.json (en puntos PDF)</label>
      <pre class="fb-demo__json">{{ templateJson }}</pre>
      </template>

      <!-- ===== Panel de COMPLETAR ===== -->
      <template v-else>
      <p class="fb-demo__hint">
        Completa los campos definidos en la plantilla. Los valores se guardan
        aparte, sin modificar la definición de campos.
      </p>
      <label class="fb-demo__label">values (por id de campo)</label>
      <pre class="fb-demo__json">{{ valuesJson }}</pre>
      </template>
    </aside>

    <main class="fb-demo__main">
      <template v-if="src">
        <!-- v-show (no v-if): vue-pdf-signature destruye el worker PDF.js global
             al desmontar una instancia, dejando a la otra sin render. Mantener
             ambos montados y solo ocultar el inactivo evita ese problema. -->
        <PdfFieldEditor
          v-show="mode === 'editor'"
          :active="mode === 'editor'"
          v-model="fields"
          :s3url="src"
          :active-field-type="activeFieldType"
          :allowed-field-types="allowedFieldTypes"
          :selected-id.sync="selectedId"
          name="demo.pdf"
          @field-created="onFieldCreated"
        />
        <PdfFormViewer
          v-show="mode === 'fill'"
          :active="mode === 'fill'"
          v-model="values"
          :fields="fields"
          :s3url="src"
          name="demo.pdf"
        />
      </template>
      <div v-else class="fb-demo__empty">
        Pega una URL de PDF y pulsa «Cargar».
      </div>
    </main>
  </div>
</template>

<script>

import PdfFieldEditor from '@/febos/global/pdf/formBuilder/components/PdfFieldEditor';
import PdfFormViewer from '@/febos/global/pdf/formBuilder/components/PdfFormViewer';
import FieldListPanel from '@/febos/global/pdf/formBuilder/components/FieldListPanel';
import {
  DATA_FIELD_TYPES,
  defaultLabelForType,
  valueKeyOf,
} from '@/febos/global/pdf/formBuilder/model/fieldModel';

const DEFAULT_PDF = 'https://archivos.febos.io/temporal/ae573014-2982-44e0-a10e-8d0c0417900d/9cec66ca-fd0f-4cae-83ff-d8ea95b00f4f.pdf';

export default {
  name: 'FormBuilderDemo',
  components: { PdfFieldEditor, PdfFormViewer, FieldListPanel },
  data() {
    return {
      mode: 'editor',
      urlInput: DEFAULT_PDF,
      src: DEFAULT_PDF,
      allowedFieldTypes: [...DATA_FIELD_TYPES],
      activeFieldType: DATA_FIELD_TYPES[0],
      selectedId: null,
      fields: [],
      values: {},
    };
  },
  computed: {
    fieldTypeList() {
      return this.allowedFieldTypes.map((type) => ({
        type,
        label: defaultLabelForType(type),
      }));
    },
    activeLabel() {
      return this.activeFieldType ? defaultLabelForType(this.activeFieldType) : '';
    },
    valuesJson() {
      // Mapea nombre->valor (por clave de dato) para que sea legible en la demo.
      const byLabel = {};
      this.fields.forEach((f) => {
        const v = this.values[valueKeyOf(f)];
        byLabel[f.name] = v != null ? v : '';
      });
      return JSON.stringify({ values: this.values, porNombre: byLabel }, null, 2);
    },
    templateJson() {
      return JSON.stringify(
        {
          version: 1,
          fields: this.fields.map((f) => ({
            id: f.id,
            type: f.type,
            name: f.name,
            label: f.label,
            page: f.page,
            x: round(f.x),
            y: round(f.y),
            width: round(f.width),
            height: round(f.height),
            required: f.required,
            assignedTo: f.assignedTo,
          })),
        },
        null,
        2,
      );
    },
  },
  methods: {
    loadUrl() {
      this.src = this.urlInput.trim();
    },
    toggleType(type) {
      // Click en el mismo tipo lo deselecciona; de otro modo lo activa.
      this.activeFieldType = this.activeFieldType === type ? null : type;
    },
    clearFields() {
      this.fields = [];
      this.selectedId = null;
    },
    deleteField(id) {
      this.fields = this.fields.filter((f) => f.id !== id);
      if (this.selectedId === id) {
        this.selectedId = null;
      }
    },
    updateField(updated) {
      this.fields = this.fields.map((f) => (f.id === updated.id ? updated : f));
    },
    onFieldCreated() {
      // Modo "sello único": tras agregar, se deselecciona el tipo para que un
      // doble click no cree un segundo campo. Se vuelve a elegir para otro.
      this.activeFieldType = null;
    },
  },
};

function round(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

</script>

<style lang="scss" scoped>

.fb-demo {
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

.fb-demo__sidebar {
  width: 340px;
  min-width: 340px;
  padding: 16px;
  background: #f8f8fb;
  border-right: 1px solid #e3e3ef;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.fb-demo__title {
  margin-bottom: 12px;
}

.fb-demo__mode {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
}

.fb-demo__mode > * {
  flex: 1;
}

.fb-demo__label {
  font-size: 12px;
  font-weight: 600;
  color: #6b6b85;
  margin: 10px 0 4px;
}

.fb-demo__url {
  display: flex;
  gap: 6px;
}

.fb-demo__input {
  flex: 1;
  min-width: 0;
  padding: 4px 8px;
  border: 1px solid #d8d8e3;
  border-radius: 4px;
  font-size: 12px;
}

.fb-demo__palette {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.fb-demo__palette-btn {
  flex: 0 0 auto;
}

.fb-demo__hint {
  font-size: 11px;
  color: #8a8aa0;
  margin: 10px 0;
}

.fb-demo__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px 0;
}

.fb-demo__count {
  font-size: 12px;
  color: #6b6b85;
}

.fb-demo__list {
  margin-bottom: 4px;
}

.fb-demo__json {
  background: #1e1e2d;
  color: #c3e88d;
  font-size: 11px;
  padding: 10px;
  border-radius: 4px;
  overflow: auto;
  max-height: 220px;
  white-space: pre;
}

.fb-demo__main {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.fb-demo__empty {
  margin: auto;
  color: #8a8aa0;
}

</style>
